import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';

import { DrawerModule } from 'primeng/drawer';
import { SkeletonModule } from 'primeng/skeleton';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { TabsModule } from 'primeng/tabs';
import { forkJoin } from 'rxjs';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { InformationCircleIcon } from '@hugeicons/core-free-icons';
import {
  EscalaService,
  EfetivoService,
  TurnoService,
  OcorrenciaService,
  EscalaUpdateDto,
} from '@core/services/domain-services';
import { BackButtonComponent } from '@shared/components/back-button/back-button.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { EscalaHistoricoComponent } from '@shared/components/escala-historico/escala-historico.component';
import { obterEstadoEscalaVisual } from '../escala-estado.util';

interface EfectivoCard {
  key: string;
  nome: string;
  foto: string | null;
  funcao: string;
  presencaLabel: string;
  presencaClass: string;
  entradaPrevista: string;
  entradaReal: string;
  entradaRegistada: boolean;
  saida: string;
  saidaRegistada: boolean;
  turnoTerminado: boolean;
  observacaoFim: string | null;
  avaliacaoFim: number | null;
}

interface HistoricoEvento {
  key: string;
  titulo: string;
  detalhe: string | null;
  autor: string | null;
  quandoLabel: string;
  icone: string;
  cor: string;
  ordem: number;
}

@Component({
  selector: 'app-escala-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,

    DrawerModule,
    SkeletonModule,
    SelectModule,
    DatePickerModule,
    TabsModule,
    HugeiconsIconComponent,
    BackButtonComponent,
    EmptyStateComponent,
    EscalaHistoricoComponent,
  ],

  templateUrl: './escala-detail.component.html',
  styleUrl: './escala-detail.component.scss',
})
export class EscalaDetailComponent implements OnInit {
  readonly ocorrenciasIcon = InformationCircleIcon;

  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly escalaSvc = inject(EscalaService);
  private readonly efetivoSvc = inject(EfetivoService);
  private readonly turnoSvc = inject(TurnoService);
  private readonly ocorrenciaSvc = inject(OcorrenciaService);
  private readonly msg = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  // Angular 21 corre sem zone.js (zoneless): callbacks assíncronos têm de
  // marcar a view para a change detection ser agendada — ver §20.
  private readonly cdr = inject(ChangeDetectorRef);

  escala: any = null;
  escalaId = 0;
  loading = true;

  activeTab = '0';

  todosEfetivos: any[] = [];
  turnos: any[] = [];
  loadingDrops = false;

  efectivoCards: EfectivoCard[] = [];

  historicoEventos: HistoricoEvento[] = [];
  loadingHistorico = false;
  historicoCarregado = false;

  ocorrencias: any[] = [];
  ocorrenciasTotal = 0;
  loadingOcorrencias = false;

  // Drawer: Adicionar Efectivo
  showAddEfetivoDialog = false;
  addingEfetivo = false;
  efetivoParaAdicionar: number | null = null;

  // Drawer: Editar Escala
  showEditDialog = false;
  savingEdit = false;
  editForm!: FormGroup;

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const id = Number(params.get('id'));
        if (!id) return;
        this.escalaId = id;
        this.loading = true;
        this.historicoCarregado = false;
        this.historicoEventos = [];
        this.loadEscala();
        this.loadOcorrencias();
        this.loadDropdowns();
        this.cdr.markForCheck();
      });
  }

  // ── Carregamento ──────────────────────────────────────────
  loadEscala(): void {
    this.escalaSvc
      .getById(this.escalaId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.escala = res?.data ?? res;
          this.rebuildEfectivoCards();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  loadDropdowns(): void {
    this.loadingDrops = true;
    forkJoin({
      efetivos: this.efetivoSvc.getAll({ pageSize: 200 }),
      turnos: this.turnoSvc.getAll({ pageSize: 100 }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.todosEfetivos = Array.isArray(res.efetivos?.data)
            ? res.efetivos.data
            : [];
          this.turnos = Array.isArray(res.turnos?.data) ? res.turnos.data : [];
          this.loadingDrops = false;
          this.rebuildEfectivoCards();
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingDrops = false;
          this.cdr.markForCheck();
        },
      });
  }

  loadHistorico(): void {
    if (this.historicoCarregado || this.loadingHistorico) return;
    this.loadingHistorico = true;
    this.cdr.markForCheck();
    this.escalaSvc
      .getHistorico(this.escalaId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const payload = res?.data ?? res;
          this.historicoEventos = this.buildHistoricoEventos(payload);
          this.historicoCarregado = true;
          this.loadingHistorico = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.historicoEventos = this.buildHistoricoEventos(null);
          this.historicoCarregado = true;
          this.loadingHistorico = false;
          this.cdr.markForCheck();
        },
      });
  }

  loadOcorrencias(): void {
    this.loadingOcorrencias = true;
    this.cdr.markForCheck();
    this.ocorrenciaSvc
      .getAll({
        IdEscala: this.escalaId,
        pageNumber: 1,
        pageSize: 100,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.ocorrencias = Array.isArray(res?.data) ? res.data : [];
          this.ocorrenciasTotal = Number(
            res?.totalRecords ?? this.ocorrencias.length,
          );
          this.loadingOcorrencias = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.ocorrencias = [];
          this.ocorrenciasTotal = 0;
          this.loadingOcorrencias = false;
          this.cdr.markForCheck();
        },
      });
  }

  // ── Tabs ──────────────────────────────────────────────────
  onTabChange(value: string | number | undefined | null): void {
    if (value === null || value === undefined) return;
    const tab = String(value);
    this.activeTab = tab;
    if (tab === '1') this.loadHistorico();
    this.cdr.markForCheck();
  }

  // ── Cards de efectivo ─────────────────────────────────────
  private rebuildEfectivoCards(): void {
    this.efectivoCards = this.efetivosNaEscala.map(
      (item: any, index: number) => this.buildEfectivoCard(item, index),
    );
  }

  private buildEfectivoCard(item: any, index: number): EfectivoCard {
    const efetivoId = Number(item?.id);
    const catalogo = this.todosEfetivos.find(
      (e) => Number(e?.id) === efetivoId,
    );

    const nome = String(
      item?.nome ?? `Efectivo #${efetivoId || index + 1}`,
    );

    const foto = catalogo?.urlFotografia ?? catalogo?.foto ?? null;

    const partes: string[] = [];
    if (catalogo?.nomeFuncao) {
      partes.push(String(catalogo.nomeFuncao));
    } else if (item?.papelEfetivoEscala) {
      partes.push(this.humanizar(item.papelEfetivoEscala));
    }
    if (catalogo?.nomeCategoria) {
      partes.push(String(catalogo.nomeCategoria));
    }

    const hasFalta = item?.hasFalta === true;
    const prevista = this.getEntradaPrevistaDate();
    const atrasoMin = this.calcularAtrasoMin(prevista, item?.horaInicioReal);

    let presencaLabel: string;
    let presencaClass: string;
    if (hasFalta) {
      presencaLabel = 'Falta';
      presencaClass = 'presenca-falta';
    } else if (atrasoMin !== null && atrasoMin > 0) {
      presencaLabel = `Atraso de ${this.formatarDuracaoAtraso(atrasoMin)}`;
      presencaClass = 'presenca-atraso';
    } else if (item?.horaFimReal) {
      presencaLabel = 'Concluído';
      presencaClass = 'presenca-concluido';
    } else if (item?.horaInicioReal) {
      presencaLabel = 'Em serviço';
      presencaClass = 'presenca-em-servico';
    } else {
      presencaLabel = 'Planeado';
      presencaClass = 'presenca-planeado';
    }

    return {
      key: `${efetivoId || index}`,
      nome,
      foto,
      funcao: partes.join(' · '),
      presencaLabel,
      presencaClass,
      entradaPrevista: this.formatDataHoraCurta(prevista),
      entradaReal: item?.horaInicioReal
        ? this.formatDataHoraCurta(
            this.parseHoraReal(item.horaInicioReal, prevista),
          )
        : '—',
      entradaRegistada: !!item?.horaInicioReal,
      saida: item?.horaFimReal
        ? this.formatDataHoraCurta(
            this.parseHoraReal(item.horaFimReal, prevista),
          )
        : 'Ainda não registada',
      saidaRegistada: !!item?.horaFimReal,
      turnoTerminado: !!item?.horaFimReal,
      observacaoFim: item?.observacaoFim ?? null,
      avaliacaoFim:
        item?.avaliacaoFim !== null && item?.avaliacaoFim !== undefined
          ? Number(item.avaliacaoFim)
          : null,
    };
  }

  get efetivosNaEscala(): any[] {
    const value = this.escala as any;
    return Array.isArray(value?.efetivos) ? value.efetivos : [];
  }

  get efetivosDisponiveis(): any[] {
    const idsNaEscala = new Set(
      this.efetivosNaEscala.map((e: any) => Number(e?.id)),
    );
    return this.todosEfetivos.filter((e) => !idsNaEscala.has(Number(e?.id)));
  }

  // ── Histórico ─────────────────────────────────────────────
  private buildHistoricoEventos(payload: any): HistoricoEvento[] {
    const eventos: HistoricoEvento[] = [];

    const criacao = this.parseApiDate((this.escala as any)?.dataCriacao);
    if (criacao) {
      eventos.push({
        key: 'criacao',
        titulo: 'Escala criada',
        detalhe: null,
        autor: null,
        quandoLabel: this.formatDataHoraCurta(criacao),
        icone: 'ri-calendar-check-line',
        cor: 'blue',
        ordem: criacao.getTime(),
      });
    }

    if (!payload || typeof payload !== 'object') {
      return eventos;
    }

    const efetivos = Array.isArray(payload.efetivos) ? payload.efetivos : [];
    for (const ef of efetivos) {
      const autor =
        ef?.nome ?? ef?.efetivoNome ?? ef?.efetivo?.nome ?? null;

      if (ef?.horaInicioReal) {
        const quando = this.parseApiDate(ef.horaInicioReal);
        eventos.push({
          key: `entrada-${ef?.efetivoId ?? ef?.id ?? eventos.length}`,
          titulo: 'Registo de entrada',
          detalhe: null,
          autor,
          quandoLabel: this.formatDataHoraCurta(quando),
          icone: 'ri-login-box-line',
          cor: 'green',
          ordem: quando?.getTime() ?? 0,
        });
      }

      const faltas = Array.isArray(ef?.faltas) ? ef.faltas : [];
      for (const [i, falta] of faltas.entries()) {
        const quando =
          this.parseApiDate(falta?.dataFaltaInicio) ??
          this.parseApiDate(falta?.data) ??
          this.parseApiDate(ef?.horaInicioReal);
        eventos.push({
          key: `falta-${ef?.efetivoId ?? ef?.id ?? i}-${i}`,
          titulo: 'Falta registada',
          detalhe: falta?.motivo ?? falta?.observacao ?? null,
          autor,
          quandoLabel: this.formatDataHoraCurta(quando),
          icone: 'ri-user-unfollow-line',
          cor: 'red',
          ordem: quando?.getTime() ?? 0,
        });
      }

      if (ef?.horaFimReal) {
        const quando = this.parseApiDate(ef.horaFimReal);
        eventos.push({
          key: `saida-${ef?.efetivoId ?? ef?.id ?? eventos.length}`,
          titulo: 'Registo de saída',
          detalhe: null,
          autor,
          quandoLabel: this.formatDataHoraCurta(quando),
          icone: 'ri-logout-box-line',
          cor: 'purple',
          ordem: quando?.getTime() ?? 0,
        });
      }
    }

    const rondas = Array.isArray(payload.rondas) ? payload.rondas : [];
    for (const r of rondas) {
      const quando = this.parseApiDate(r?.dataInicio);
      const totalP = Number(
        r?.totalPontosCriticos ?? r?.pontosCriticos?.length ?? 0,
      );
      const totalC = Number(r?.totalPontosCriticosChecados ?? 0);
      const concluida = r?.concluida === true;
      eventos.push({
        key: `ronda-${r?.id ?? rondas.indexOf(r)}`,
        titulo: concluida
          ? `Ronda concluída #${r?.id}`
          : `Ronda em curso #${r?.id}`,
        detalhe: `${totalC}/${totalP} pontos críticos verificados`,
        autor: r?.efetivoNome ?? null,
        quandoLabel: this.formatDataHoraCurta(quando),
        icone: 'ri-route-line',
        cor: concluida ? 'blue' : 'amber',
        ordem: quando?.getTime() ?? 0,
      });
    }

    return eventos.sort((a, b) => b.ordem - a.ordem);
  }

  // ── Adicionar Efectivo ────────────────────────────────────
  openAddEfetivo(): void {
    this.efetivoParaAdicionar = null;
    this.showAddEfetivoDialog = true;
  }

  confirmarAdicionarEfetivo(): void {
    if (!this.efetivoParaAdicionar) {
      this.msg.add({
        severity: 'warn',
        summary: 'Seleccione um efectivo',
        life: 3000,
      });
      this.cdr.markForCheck();
      return;
    }
    this.addingEfetivo = true;
    this.cdr.markForCheck();
    this.escalaSvc
      .adicionarEfetivo(this.escalaId, this.efetivoParaAdicionar)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.msg.add({
            severity: 'success',
            summary: 'Efectivo adicionado',
            life: 3000,
          });
          this.showAddEfetivoDialog = false;
          this.addingEfetivo = false;
          this.loadEscala();
          this.cdr.markForCheck();
        },
        error: () => {
          this.msg.add({
            severity: 'error',
            summary: 'Erro ao adicionar efectivo',
            life: 3000,
          });
          this.addingEfetivo = false;
          this.cdr.markForCheck();
        },
      });
  }

  // ── Editar Escala ─────────────────────────────────────────
  openEdit(): void {
    if (!this.escala) return;
    this.editForm = this.fb.group({
      turnoId: [this.escala.turnoId, Validators.required],
      dataInicio: [this.toDate(this.escala.dataInicio), Validators.required],
      dataTermino: [
        this.toDate(this.escala.dataFim ?? this.escala.dataTermino),
        Validators.required,
      ],
    });
    this.showEditDialog = true;
  }

  saveEdit(): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }
    this.savingEdit = true;
    this.cdr.markForCheck();
    const v = this.editForm.value;
    const payload: EscalaUpdateDto = {
      postoId: Number(this.escala?.idPosto),
      turnoId: Number(v.turnoId),
      dataInicio: this.toISO(v.dataInicio),
      dataTermino: this.toISO(v.dataTermino),
      estado: Number(this.escala?.estado),
      escalasEfetivosAddOrUpdate: this.efetivosNaEscala
        .filter((ef: any) => Number(ef?.id) > 0)
        .map((ef: any) => ({
          efetivoId: Number(ef.id),
          horaInicioReal: ef.horaInicioReal ?? null,
          horaFimReal: ef.horaFimReal ?? null,
          papelEfetivoEscalaEnum: ef.papelEfetivoEscalaEnum,
        })),
    };
    this.escalaSvc
      .updateEscala(this.escalaId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.msg.add({
            severity: 'success',
            summary: 'Escala actualizada',
            life: 3000,
          });
          this.showEditDialog = false;
          this.savingEdit = false;
          this.loadEscala();
          this.cdr.markForCheck();
        },
        error: () => {
          this.msg.add({
            severity: 'error',
            summary: 'Erro ao guardar',
            life: 3000,
          });
          this.savingEdit = false;
          this.cdr.markForCheck();
        },
      });
  }

  // ── Datas e formatação ────────────────────────────────────
  private getEntradaPrevistaDate(): Date | null {
    const base = this.parseApiDate((this.escala as any)?.dataInicio);
    if (!base) return null;
    const horaTurno = this.turnoSelecionado?.horaInicio;
    if (horaTurno) {
      const match = String(horaTurno).match(/(\d{1,2}):(\d{2})/);
      if (match) {
        base.setHours(Number(match[1]), Number(match[2]), 0, 0);
      }
    }
    return base;
  }

  private parseApiDate(value: unknown): Date | null {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : new Date(value);
    }
    const raw = String(value).trim();
    const normalized =
      raw.includes(' ') && !raw.includes('T') ? raw.replace(' ', 'T') : raw;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private parseHoraReal(
    value: string | null | undefined,
    base: Date | null,
  ): Date | null {
    if (!value) return null;
    const raw = String(value).trim();

    // Apenas hora (HH:mm ou HH:mm:ss): combinar com a data prevista.
    const timeMatch = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (timeMatch) {
      if (!base) return null;
      const d = new Date(base);
      d.setHours(
        Number(timeMatch[1]),
        Number(timeMatch[2]),
        timeMatch[3] ? Number(timeMatch[3]) : 0,
        0,
      );
      // Turno noturno: a entrada real pode pertencer ao dia seguinte.
      if (d.getTime() < base.getTime() - 12 * 3_600_000) {
        d.setDate(d.getDate() + 1);
      }
      return d;
    }

    // Data/hora completa (yyyy-MM-dd[THH:mm[:ss]]).
    const isoMatch = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?/,
    );
    if (isoMatch) {
      return new Date(
        Number(isoMatch[1]),
        Number(isoMatch[2]) - 1,
        Number(isoMatch[3]),
        isoMatch[4] ? Number(isoMatch[4]) : 0,
        isoMatch[5] ? Number(isoMatch[5]) : 0,
        isoMatch[6] ? Number(isoMatch[6]) : 0,
      );
    }

    return this.parseApiDate(raw);
  }

  private calcularAtrasoMin(
    entradaPrevista: Date | null,
    horaInicioReal: string | null,
  ): number | null {
    if (!horaInicioReal || !entradaPrevista) return null;
    const real = this.parseHoraReal(horaInicioReal, entradaPrevista);
    if (!real) return null;
    const diff = real.getTime() - entradaPrevista.getTime();
    if (diff <= 0) return 0;
    return Math.max(1, Math.round(diff / 60_000));
  }

  private formatarDuracaoAtraso(minutos: number): string {
    const horas = Math.floor(minutos / 60);
    const minutosRestantes = minutos % 60;

    if (horas === 0) return `${minutos}min`;
    if (minutosRestantes === 0) return `${horas}h`;
    return `${horas}h:${minutosRestantes}min`;
  }

  private humanizar(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    if (!texto) return '';
    return texto.charAt(0).toUpperCase() + texto.slice(1).toLowerCase();
  }

  formatDataHoraCurta(d: Date | null): string {
    if (!d) return '—';
    const dia = new Intl.DateTimeFormat('pt-AO', {
      day: '2-digit',
      month: '2-digit',
    }).format(d);
    const hora = new Intl.DateTimeFormat('pt-AO', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
    return `${dia} · ${hora}`;
  }

  private formatHoraCurta(d: Date | null): string {
    if (!d) return '—';
    return new Intl.DateTimeFormat('pt-AO', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  }

  formatDate(d?: string | Date | null): string {
    if (!d) return '—';
    const parsed = d instanceof Date ? d : this.parseApiDate(d);
    if (!parsed) return '—';
    return new Intl.DateTimeFormat('pt-AO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(parsed);
  }

  // ── Getters de apresentação ───────────────────────────────
  get estadoLabel(): string {
    const descricao = (this.escala as any)?.estadoDescricao;
    return descricao ? String(descricao) : '';
  }

  get podeEditarEscala(): boolean {
    const estado = obterEstadoEscalaVisual(this.escala);
    return estado === 'agendada' || estado === 'decorrendo';
  }

  get turnoSelecionado(): any {
    const value = this.escala as any;
    const turnoId = Number(value?.turnoId);
    if (!turnoId) return null;
    return this.turnos.find((t) => Number(t?.id) === turnoId) ?? null;
  }

  get tipoTurnoLabel(): string {
    const value = this.escala as any;
    return (
      value?.nomeTurno ??
      this.turnoSelecionado?.designacao ??
      this.turnoSelecionado?.nome ??
      ''
    );
  }

  get tipoTurnoClass(): string {
    const nome = this.tipoTurnoLabel.toLowerCase();
    if (!nome) return '';
    if (nome.includes('24')) return 'badge-24h';
    if (nome.includes('noturn')) return 'badge-noturno';
    if (nome.includes('diurn')) return 'badge-diurno';
    return 'badge-turno';
  }

  get horarioTurnoLabel(): string {
    const t = this.turnoSelecionado;
    if (!t?.horaInicio) return '';
    const ini = String(t.horaInicio).slice(0, 5);
    const fim = t.horaFim ? String(t.horaFim).slice(0, 5) : null;
    return fim ? `${ini} – ${fim}` : ini;
  }

  get criacaoLabel(): string {
    const criacao = this.parseApiDate((this.escala as any)?.dataCriacao);
    if (!criacao) return '';
    return `Criada em ${this.formatDate(criacao)} às ${this.formatHoraCurta(criacao)}`;
  }

  get kpiPeriodo(): string {
    const value = this.escala as any;
    const ini = this.parseApiDate(value?.dataInicio);
    const fim = this.parseApiDate(value?.dataFim ?? value?.dataTermino);
    if (!ini || !fim) return '—';
    const ms = fim.getTime() - ini.getTime();
    if (ms <= 0) return '—';
    const dias = Math.max(1, Math.ceil(ms / 86_400_000));
    return dias === 1 ? '1 dia' : `${dias} dias`;
  }

  get kpiDuracao(): string {
    const value = this.escala as any;
    const ini = this.parseApiDate(value?.dataInicio);
    const fim = this.parseApiDate(value?.dataFim ?? value?.dataTermino);
    if (!ini || !fim) return '—';
    const totalMin = Math.round((fim.getTime() - ini.getTime()) / 60_000);
    if (totalMin <= 0) return '—';
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h === 0) return `${m} min`;
    return m ? `${h}h ${m}min` : `${h}h`;
  }

  getEstadoClass(): string {
    const label = this.estadoLabel.toLowerCase();
    if (label.includes('decorr') || label.includes('exec'))
      return 'estado-decorrendo';
    if (label.includes('agend')) return 'estado-agendada';
    if (label.includes('conclu')) return 'estado-concluida';
    return 'estado-default';
  }

  getInitials(nome: string): string {
    return (nome ?? '')
      .split(' ')
      .filter((n) => n.length > 0)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  trackByCard(_index: number, card: EfectivoCard): string {
    return card.key;
  }

  trackByEvento(_index: number, evento: HistoricoEvento): string {
    return evento.key;
  }

  private toDate(d?: string | null): Date | null {
    return this.parseApiDate(d);
  }

  private toISO(d: Date): string {
    if (!d) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`;
  }
}
